import * as fx from '../audio/fx';
import { $, $$, reducedMotion } from './dom';

export type Tab = 'tasks' | 'stats' | 'settings' | 'look';

const ALL_TABS: Tab[] = ['tasks', 'stats', 'settings', 'look'];

const sheet = $('#sheet');
const scrim = $('#scrim');
const appRoot = $('#app');
const tabs = $$<HTMLButtonElement>('#sheet [role="tab"]');
let current: Tab = 'tasks';
let opener: HTMLElement | null = null;
let hideTimer: ReturnType<typeof setTimeout> | undefined;
const tabListeners: ((t: Tab) => void)[] = [];
/** Panes a layout shows on the main screen instead (Desk: tasks, Logbook: stats). */
const docked = new Set<Tab>();
const available = () => ALL_TABS.filter((t) => !docked.has(t));

export const isSheetOpen = () => sheet.classList.contains('on');
export const currentTab = () => current;
export const onTabShown = (fn: (t: Tab) => void) => tabListeners.push(fn);

function setTab(t: Tab, focusTab = false) {
  current = t;
  for (const b of tabs) {
    const on = b.dataset.tab === t;
    b.classList.toggle('on', on);
    b.setAttribute('aria-selected', String(on));
    b.tabIndex = on ? 0 : -1;
    if (on && focusTab) b.focus();
  }
  for (const p of $$('#sheet .pane')) p.classList.toggle('on', p.dataset.pane === t);
  $('#sheetTitle').textContent = t[0]!.toUpperCase() + t.slice(1);
  syncPills();
  tabListeners.forEach((fn) => fn(t));
}

function syncPills() {
  const open = isSheetOpen();
  for (const [id, tab] of [
    ['#tasksBtn', 'tasks'],
    ['#statsBtn', 'stats'],
    ['#settingsBtn', 'settings'],
  ] as const) {
    const b = $(id);
    const on = open && current === tab;
    b.classList.toggle('on', on);
    b.setAttribute('aria-expanded', String(on));
  }
}

/** Hides the docked panes' tabs; their header buttons then jump to the main screen instead. */
export function setDocked(list: Tab[]) {
  docked.clear();
  for (const t of list) docked.add(t);
  for (const b of tabs) b.hidden = docked.has(b.dataset.tab as Tab);
  if (docked.has(current)) setTab(available()[0]!);
}

function revealDocked(tab: Tab) {
  closeSheet();
  const panel = $('#sidePanel');
  panel.scrollIntoView({ block: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth' });
  if (tab === 'tasks') $('#taskInput').focus({ preventScroll: true });
  else panel.focus({ preventScroll: true });
}

export function openSheet(tab: Tab) {
  if (docked.has(tab)) return revealDocked(tab);
  clearTimeout(hideTimer);
  const wasOpen = isSheetOpen();
  if (!wasOpen) opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  sheet.hidden = false;
  void sheet.offsetWidth; // let the slide-in transition run from the hidden state
  sheet.classList.add('on');
  scrim.classList.add('on');
  appRoot.inert = true;
  fx.tap();
  setTab(tab);
  if (tab === 'tasks') setTimeout(() => $('#taskInput').focus(), 80);
  else if (!wasOpen) $<HTMLButtonElement>(`#tab-${tab}`).focus();
}

export function closeSheet() {
  if (!isSheetOpen()) return;
  sheet.classList.remove('on');
  scrim.classList.remove('on');
  appRoot.inert = false;
  syncPills();
  hideTimer = setTimeout(() => (sheet.hidden = true), reducedMotion() ? 0 : 500);
  opener?.focus();
  opener = null;
}

export const toggleSheet = (tab: Tab) =>
  isSheetOpen() && current === tab && !docked.has(tab) ? closeSheet() : openSheet(tab);

const FOCUSABLE =
  'button:not([disabled]), input:not([type="file"]):not([disabled]), textarea, select, a[href], [tabindex]';

/**
 * Keeps Tab inside the dialog. Toast buttons (UNDO) count as part of it, so they stay reachable.
 * Listens on the document: if a re-render dropped focus to <body>, Tab must still land inside.
 */
function trapFocus(e: KeyboardEvent) {
  if (e.key !== 'Tab' || !isSheetOpen()) return;
  const toasts = $('#toasts');
  const focusables = [
    ...$$<HTMLElement>(FOCUSABLE, sheet),
    ...$$<HTMLElement>('.toast:not(.out) button', toasts),
  ]
    // roving-tabindex tabs are -1 unless selected; hidden panes have no client rects
    .filter((el) => el.tabIndex >= 0 && el.getClientRects().length > 0);
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  if (!first || !last) return;
  const a = document.activeElement;
  const inside = a instanceof Node && (sheet.contains(a) || toasts.contains(a));
  if (!inside) {
    e.preventDefault();
    (e.shiftKey ? last : first).focus();
  } else if (e.shiftKey && a === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && a === last) {
    e.preventDefault();
    first.focus();
  }
}

export function initSheet() {
  for (const b of tabs) b.addEventListener('click', () => setTab(b.dataset.tab as Tab));
  $('#sheet [role="tablist"]').addEventListener('keydown', (e) => {
    const order = available();
    const i = order.indexOf(current);
    const n = order.length;
    const next =
      e.key === 'ArrowRight'
        ? order[(i + 1) % n]
        : e.key === 'ArrowLeft'
          ? order[(i + n - 1) % n]
          : e.key === 'Home'
            ? order[0]
            : e.key === 'End'
              ? order[n - 1]
              : null;
    if (!next) return;
    e.preventDefault();
    setTab(next, true);
  });
  $('#tasksBtn').addEventListener('click', () => toggleSheet('tasks'));
  $('#statsBtn').addEventListener('click', () => toggleSheet('stats'));
  $('#settingsBtn').addEventListener('click', () => toggleSheet('settings'));
  $('#closeSheet').addEventListener('click', closeSheet);
  scrim.addEventListener('click', closeSheet);
  $('#statCol').addEventListener('click', () => openSheet('stats'));
  $('#nowTask').addEventListener('click', () => openSheet('tasks'));
  document.addEventListener('keydown', trapFocus);
  setTab('tasks');
  syncPills();
}
